import type { Store } from 'redux';
import { type UseAnalyticsEventsHook, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies';
import { useEvent } from '@atlassian/jira-software-react-use-event';
import { DND_TARGET_DIVIDER } from '../../../common/constants';
import {
	BEFORE_CARD,
	BOTTOM_OF_CARD_GROUP,
	BOTTOM_OF_LIST,
	TOP_OF_LIST,
} from '../../../model/constants';
import type { Card, CardId, CardListId } from '../../../model/types';
import { type CardsMovePayload, cardsMove } from '../../actions/cards/move';
import { useBacklogStore } from '../../index';
import type { CardOrderEntityState } from '../../reducers/entities/card-order/types';
import type { CardEntityState } from '../../reducers/entities/card/types';
import { isCMPScrumBacklog } from '../../selectors/app';
import {
	getCards,
	getFilteredOrderedCardIdsByCardListId,
	getOrderedCardIdsByCardListId,
	getOrderedCardListIds,
	getSelectedCards,
	makeGetCardGroupExpanded,
} from '../../selectors/cards';
import { makeIsAllSelectedSiblingSubtasks } from '../../selectors/selected-cards';
import { makeIsCardListActiveSprint } from '../../selectors/sprints';
import type { Action, State } from '../../types';

/**
 * VISIBLE FOR TESTING
 *
 * Returns a card's listId and its index within that list.
 */
export function getCardListAndIndex(
	cardIdsByCardListId: CardOrderEntityState,
	cardId: CardId,
): { cardListId: string; index: number } | null {
	const cardListIds = Object.keys(cardIdsByCardListId);
	for (let i = 0; i < cardListIds.length; i += 1) {
		const cardListId = cardListIds[i];
		const cardIds = cardIdsByCardListId[cardListId];
		const maybeCardIndex = cardIds.indexOf(cardId);
		if (maybeCardIndex !== -1) {
			return { cardListId, index: maybeCardIndex };
		}
	}

	return null;
}

/**
 * VISIBLE FOR TESTING
 *
 * When moving a card into a target index/list, resolve the target card ID
 * above which we'd need to rank the dragged card to perform the move.
 *
 * This is needed if the user is dragging on the bottom half of a card.
 *
 * Returns undefined if the card should be dragged to the bottom of the list.
 */
export function getBeforeCardId({
	location,
	targetList,
	targetIndex,
	isCardSubtask,
	cards,
	card,
	isParentExpanded,
	cardsToMove,
}: {
	location:
		| MoveCardsTargetLocationRelativeToCard
		| MoveCardsTargetLocationRelativeToCurrentPosition;
	targetList: CardId[];
	targetIndex: number;
	isCardSubtask: boolean;
	cards: CardEntityState;
	card: Card;
	isParentExpanded?: boolean;
	cardsToMove?: CardId[];
}): { type: 'card'; beforeCardId: CardId } | { type: 'bottom' } {
	let index = Math.max(0, targetIndex);
	if (location.type === 'after') index += 1;
	// If the card to move are the target, e.g.: we're moving cards onto themselves, then we
	// scan the list to find the next non-selected card to move before.
	const cardsToMoveSet = new Set(cardsToMove ?? []);
	while (index < targetList.length && cardsToMoveSet.has(targetList[index])) {
		index += 1;
	}

	let beforeCardId: number | null = targetList[index] ?? null;

	if (
		beforeCardId != null &&
		isCardSubtask === false &&
		isSubtaskHierarchyLevel(cards[beforeCardId]?.typeHierarchyLevel) &&
		(!isParentExpanded || location.type === 'rank-down')
	) {
		// find next non subtask related to parent, if none, then set null
		const parentId = cards[beforeCardId]?.parentId;
		const offset = location.type === 'rank-down' && card?.id === parentId ? 2 : 1;
		const nextGroupOffset = targetList
			.slice(targetIndex + 1)
			.findIndex((id) => parentId !== cards[id]?.parentId);
		const offsetCard = targetList[targetIndex + nextGroupOffset + offset] ?? null;
		beforeCardId = nextGroupOffset > -1 ? offsetCard : null;
	}

	if (beforeCardId == null) {
		return { type: 'bottom' };
	}

	// When the ticket user is dragging over is a subtask then we send through the parent ID instead of the subtask id
	if (isCardSubtask === false && isSubtaskHierarchyLevel(cards[beforeCardId]?.typeHierarchyLevel)) {
		beforeCardId = cards[beforeCardId]?.parentId ?? beforeCardId;
	}

	return { type: 'card', beforeCardId };
}

/**
 * Move a card above or below another card.
 */
export interface MoveCardsTargetLocationRelativeToCard {
	type: 'before' | 'after';
	target: CardId;
}

/**
 * Move a card above or below its current position.
 */
export interface MoveCardsTargetLocationRelativeToCurrentPosition {
	type: 'rank-up' | 'rank-down';
}

/**
 * Move a card to a list
 */
export interface MoveCardsTargetLocationToList {
	type: 'list';
	targetListId: CardListId;
}

/**
 * Source type when a card is dragged
 */
export interface MoveCardsSourceCard {
	cardId: CardId;
}

/**
 * Source type when a divider is dragged
 */
export interface MoveCardsSourceDivider {
	type: 'divider';
	cardListId: CardListId;
}

/**
 * The object being dragged. Either a card or a divider.
 */
type MoveCardsSource = MoveCardsSourceCard | MoveCardsSourceDivider;

/**
 * Represents a simplified location to move cards to.
 */
type MoveCardsTargetLocation =
	| MoveCardsTargetLocationRelativeToCard
	| MoveCardsTargetLocationRelativeToCurrentPosition
	| MoveCardsTargetLocationToList;

/**
 * Simplified payload for moving cards.
 *
 * Please do not make this complicated. The idea is to make it easy to dispatch ranking
 * actions, unlike the cardsMove action which requires the views to resolve business logic
 * such as if the movement is cross-list or not.
 */
export type SimpleMoveCardsPayload = {
	source: MoveCardsSource;
	location: MoveCardsTargetLocation;
};

const getSubtasksIfShouldMoveWithParent = (
	state: State,
	cardIdsByCardListId: CardOrderEntityState,
	cards: CardEntityState,
	cardsToMove: number[],
) => {
	const isCardGroupCollapsed = (parentId: number) => !makeGetCardGroupExpanded(state)(parentId);

	// If it's a CMP scum project, move all subtasks with parent regardless of expansion state
	// Otherwise, move the subtasks with the parent only if it is collapsed
	const shouldMoveSubtasksIfExpanded = isCMPScrumBacklog(state);
	const cardsToCheck = shouldMoveSubtasksIfExpanded
		? cardsToMove
		: cardsToMove.filter(isCardGroupCollapsed);

	return cardsToCheck.flatMap((cardGroupId) => {
		const list = getCardListAndIndex(cardIdsByCardListId, cardGroupId);
		return list
			? cardIdsByCardListId[list.cardListId].filter(
					(cardId) =>
						isSubtaskHierarchyLevel(cards[cardId]?.typeHierarchyLevel) &&
						cards[cardId]?.parentId === cardGroupId,
				)
			: [];
	});
};

export function moveCardsRelativeToCard(
	state: State,
	source: MoveCardsSourceCard,
	location: MoveCardsTargetLocationRelativeToCard,
	createAnalyticsEvent: UseAnalyticsEventsHook['createAnalyticsEvent'],
): Action | null {
	const cardIdsByCardListId = getOrderedCardIdsByCardListId(state);
	const sourceCardPosition = getCardListAndIndex(cardIdsByCardListId, source.cardId);
	const targetCardPosition = getCardListAndIndex(cardIdsByCardListId, location.target);

	if (!sourceCardPosition || !targetCardPosition) return null;

	const selectedCardIds = getSelectedCards(state);
	const cardsToMove = selectedCardIds.includes(source.cardId) ? selectedCardIds : [source.cardId];

	const cards = getCards(state);
	const card = cards[source.cardId];

	const extraCards = getSubtasksIfShouldMoveWithParent(
		state,
		cardIdsByCardListId,
		cards,
		cardsToMove,
	);
	const allCardsToMove = cardsToMove.concat(extraCards);

	const isFromActiveSprint = makeIsCardListActiveSprint(state)(sourceCardPosition.cardListId);

	const isBetweenLists =
		sourceCardPosition.cardListId !== targetCardPosition.cardListId ||
		selectedCardIds.some((cardId) =>
			Object.keys(cardIdsByCardListId).some((key) => {
				if (key === targetCardPosition.cardListId) {
					return false;
				}
				return cardIdsByCardListId[key].includes(cardId);
			}),
		);
	const basePayload: Omit<CardsMovePayload, 'placement' | 'beforeCardId'> = {
		cardIds: allCardsToMove,
		cardListId: targetCardPosition.cardListId,
		isBetweenLists,
		isFromActiveSprint,
	};
	const targetList = cardIdsByCardListId[targetCardPosition.cardListId];
	const isParentExpanded = makeGetCardGroupExpanded(state)(cards[location.target]?.id);
	const beforeCardIdResult = getBeforeCardId({
		location,
		targetList,
		targetIndex: targetCardPosition.index,
		isCardSubtask: isSubtaskHierarchyLevel(card.typeHierarchyLevel),
		cardsToMove: allCardsToMove,
		cards,
		card,
		isParentExpanded,
	});

	const event = createAnalyticsEvent({
		attributes: {
			target: targetCardPosition.cardListId,
			source: sourceCardPosition.cardListId,
		},
	});
	if (beforeCardIdResult.type === 'bottom') {
		return cardsMove(
			{
				...basePayload,
				placement: BOTTOM_OF_LIST,
			},
			event,
		);
	}

	// Ignore dragging a card onto its own leading and trailing edges
	const beforeCardIndex = targetList.indexOf(beforeCardIdResult.beforeCardId);
	const isNoopChange =
		card.id !== beforeCardIdResult.beforeCardId &&
		allCardsToMove.every(
			(cardId) =>
				beforeCardIdResult.beforeCardId === cardId || targetList[beforeCardIndex - 1] === cardId,
		);
	if (isNoopChange) {
		return null;
	}

	const isAllSelectedSiblingSubtasks: boolean = makeIsAllSelectedSiblingSubtasks(state);

	// If the card is a subtask and all selected cards are siblings, then they can only be moved in the group
	// If the "before" card doesn't have a parent it will mean that they have been moved to the last in the group
	const targetCard = cards[beforeCardIdResult.beforeCardId];
	if (
		card &&
		targetCard &&
		isAllSelectedSiblingSubtasks &&
		isSubtaskHierarchyLevel(card.typeHierarchyLevel) &&
		!isSubtaskHierarchyLevel(targetCard.typeHierarchyLevel)
	) {
		return cardsMove(
			{
				...basePayload,
				placement: BOTTOM_OF_CARD_GROUP,
			},
			event,
		);
	}

	return cardsMove(
		{
			...basePayload,
			beforeCardId: beforeCardIdResult.beforeCardId,
			placement: BEFORE_CARD,
		},
		event,
	);
}

export function moveCardsRelativeToSelf(
	state: State,
	source: MoveCardsSourceCard,
	location: MoveCardsTargetLocationRelativeToCurrentPosition,
	createAnalyticsEvent: UseAnalyticsEventsHook['createAnalyticsEvent'],
): Action | null {
	const cardIdsByCardListId = getOrderedCardIdsByCardListId(state);
	const sourceCardPosition = getCardListAndIndex(cardIdsByCardListId, source.cardId);

	if (!sourceCardPosition) return null;

	const orderedCardIds = cardIdsByCardListId[sourceCardPosition.cardListId];
	const targetIndex =
		location.type === 'rank-up' ? sourceCardPosition.index - 1 : sourceCardPosition.index + 2;

	const basePayload: Omit<CardsMovePayload, 'placement' | 'beforeCardId'> = {
		cardIds: [source.cardId],
		cardListId: sourceCardPosition.cardListId,
		isBetweenLists: false,
		isFromActiveSprint: false,
	};

	const cards = getCards(state);
	const card = cards[source.cardId];
	const beforeCardIdResult = getBeforeCardId({
		cards,
		card,
		isCardSubtask: isSubtaskHierarchyLevel(card?.typeHierarchyLevel),
		location,
		targetIndex,
		targetList: orderedCardIds,
		isParentExpanded: makeGetCardGroupExpanded(state)(cards[orderedCardIds[targetIndex]]?.id),
	});

	const event = createAnalyticsEvent({
		attributes: {
			source: sourceCardPosition.cardListId,
			target: sourceCardPosition.cardListId,
		},
	});

	if (beforeCardIdResult.type === 'bottom') {
		return cardsMove(
			{
				...basePayload,
				placement: BOTTOM_OF_LIST,
			},
			event,
		);
	}

	// If the card is a subtask and the "before" card is not, then move to the bottom of the card list
	const targetCard = cards[beforeCardIdResult.beforeCardId];
	if (
		card &&
		targetCard &&
		isSubtaskHierarchyLevel(card.typeHierarchyLevel) &&
		!isSubtaskHierarchyLevel(targetCard.typeHierarchyLevel)
	) {
		return cardsMove(
			{
				...basePayload,
				placement: BOTTOM_OF_CARD_GROUP,
			},
			event,
		);
	}

	return cardsMove(
		{
			...basePayload,
			beforeCardId: beforeCardIdResult.beforeCardId,
			placement: BEFORE_CARD,
		},
		event,
	);
}

export function moveCardsToList(
	state: State,
	source: MoveCardsSourceCard,
	location: MoveCardsTargetLocationToList,
	createAnalyticsEvent: UseAnalyticsEventsHook['createAnalyticsEvent'],
): Action | null {
	const cardIdsByCardListId = getOrderedCardIdsByCardListId(state);
	const selectedCardIds = getSelectedCards(state);
	const cardsToMove = selectedCardIds.includes(source.cardId) ? selectedCardIds : [source.cardId];
	const sourceCardPosition = getCardListAndIndex(cardIdsByCardListId, source.cardId);
	const cards = getCards(state);
	const extraCards = getSubtasksIfShouldMoveWithParent(
		state,
		cardIdsByCardListId,
		cards,
		cardsToMove,
	);
	const allCardsToMove = cardsToMove.concat(extraCards);

	// Quit if any of the cards are missing from the state
	const cardPositions = allCardsToMove.map((cardId) =>
		getCardListAndIndex(cardIdsByCardListId, cardId),
	);
	if (cardPositions.some((cardPosition) => !cardPosition)) return null;

	// Quit if noop
	const isBetweenLists = cardPositions.some(
		(cardPosition) => cardPosition?.cardListId !== location.targetListId,
	);
	if (!isBetweenLists) return null;

	// Treat as from active sprint if any of the cards is in the active sprint
	const isFromActiveSprint = cardPositions.some(
		(cardPosition) => cardPosition && makeIsCardListActiveSprint(state)(cardPosition.cardListId),
	);

	return cardsMove(
		{
			cardIds: allCardsToMove,
			cardListId: location.targetListId,
			isBetweenLists: true,
			isFromActiveSprint,
			placement: BOTTOM_OF_LIST,
		},
		createAnalyticsEvent({
			attributes: {
				source: sourceCardPosition?.cardListId,
				target: location.targetListId,
			},
		}),
	);
}

/**
 * Returns a filter function that prevents subtasks from being left behind moved without their parents
 * Pass if:
 *  card is not a subtask OR
 *  card's parent is also included in cardsToMove OR
 *  card's parent is in the list we are moving to
 */
function removeOrphanSubtasks(state: State, targetList: string) {
	return (cardId: number, index: number, cardsToMove: number[]) => {
		const cardIdsByCardListId = getFilteredOrderedCardIdsByCardListId(state);
		const cards = getCards(state);

		const isSubtask = isSubtaskHierarchyLevel(cards[cardId]?.typeHierarchyLevel);
		const { parentId } = cards[cardId];
		if (!parentId) return true;
		const parentInTargetList = cardIdsByCardListId[targetList].includes(parentId);
		const parentIsIncluded = cardsToMove.includes(parentId);

		return !isSubtask || parentIsIncluded || parentInTargetList;
	};
}

/**
 * VISIBLE FOR TESTING
 *
 * Move the divider
 */
export function moveDividerRelativeToCard(
	state: State,
	source: MoveCardsSourceDivider,
	location: MoveCardsTargetLocationRelativeToCard,
	createAnalyticsEvent: UseAnalyticsEventsHook['createAnalyticsEvent'],
): Action | null {
	const cardIdsByCardListId = getFilteredOrderedCardIdsByCardListId(state);
	const targetCardPosition = getCardListAndIndex(cardIdsByCardListId, location.target);
	if (!targetCardPosition) return null;

	const cardListOrder = getOrderedCardListIds(state);
	const sourceCardListId = source.cardListId;
	const dividerListIndex = cardListOrder.indexOf(sourceCardListId);
	if (dividerListIndex === -1) return null;

	const isFromActiveSprint = makeIsCardListActiveSprint(state)(sourceCardListId);

	const targetIndex = cardListOrder.findIndex((val) => val === targetCardPosition.cardListId);
	const sourceIndex = cardListOrder.findIndex((val) => val === source.cardListId);
	const direction = sourceIndex < targetIndex ? 'down' : 'up';

	const cardsFromOtherLists = cardListOrder
		.slice(
			direction === 'down' ? sourceIndex + 1 : targetIndex + 1,
			direction === 'down' ? targetIndex : sourceIndex + 1,
		)
		.flatMap((key) => cardIdsByCardListId[key]);

	// We will move the divider list's cards to the list under it
	// This happens when dragging the divider up into a list above
	if (direction === 'up') {
		const targetList = cardListOrder[sourceIndex + 1];
		const cardsToMove = cardIdsByCardListId[targetCardPosition.cardListId]
			.slice(location.type === 'before' ? targetCardPosition.index : targetCardPosition.index + 1)
			.concat(cardsFromOtherLists)
			.filter(removeOrphanSubtasks(state, targetList));

		return cardsMove(
			{
				cardIds: cardsToMove,
				cardListId: targetList,
				isBetweenLists: true,
				isFromActiveSprint,
				placement: TOP_OF_LIST,
			},
			createAnalyticsEvent({
				attributes: {
					type: DND_TARGET_DIVIDER,
					source: sourceCardListId,
					target: targetCardPosition.cardListId,
				},
			}),
		);
	}

	// We will move target list's cards to the divider list
	// This happens when dragging the divider down into a list under it
	const cardsToMove = cardsFromOtherLists
		.concat(
			cardIdsByCardListId[targetCardPosition.cardListId].slice(
				0,
				location.type === 'before' ? targetCardPosition.index : targetCardPosition.index + 1,
			),
		)
		.filter(removeOrphanSubtasks(state, cardListOrder[sourceIndex]));

	return cardsMove(
		{
			cardIds: cardsToMove,
			cardListId: sourceCardListId,
			isBetweenLists: true,
			isFromActiveSprint,
			placement: BOTTOM_OF_LIST,
		},
		createAnalyticsEvent({
			attributes: {
				type: DND_TARGET_DIVIDER,
				// ? With the divider here the source and target are swapped
				target: sourceCardListId,
				source: targetCardPosition.cardListId,
			},
		}),
	);
}

/**
 * VISIBLE FOR TESTING
 *
 * This is a redux-thunk style action creator for moving cards. It converts a simplified
 * payload into the right redux action.
 */
export function simplyMoveCards(
	store: Store<State>,
	{ source, location }: SimpleMoveCardsPayload,
	createAnalyticsEvent: UseAnalyticsEventsHook['createAnalyticsEvent'],
): Action | null {
	const state = store.getState();

	if (
		'type' in source &&
		source.type === 'divider' &&
		(location.type === 'before' || location.type === 'after')
	) {
		return moveDividerRelativeToCard(state, source, location, createAnalyticsEvent);
	}

	if (!('cardId' in source)) {
		return null;
	}

	if (location.type === 'before' || location.type === 'after') {
		return moveCardsRelativeToCard(state, source, location, createAnalyticsEvent);
	}

	if (location.type === 'rank-up' || location.type === 'rank-down') {
		return moveCardsRelativeToSelf(state, source, location, createAnalyticsEvent);
	}

	if (location.type === 'list') {
		return moveCardsToList(state, source, location, createAnalyticsEvent);
	}

	return null;
}

/**
 * Returns an action dispatcher for moving cards.
 */
export function useSimplyMoveCards(): (payload: SimpleMoveCardsPayload) => void {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const store = useBacklogStore();
	return useEvent((payload) => {
		const action = simplyMoveCards(store, payload, createAnalyticsEvent);
		if (action) {
			store.dispatch(action);
		}
	});
}
